import { useFormik } from 'formik';
import React, { useRef } from 'react';
import * as yup from 'yup';
import { Input, Modal } from '../ui';

interface UpdateNameDialogProps {
  isOpen: boolean;
  isUpdatingName?: boolean;
  firstName: string;
  lastName: string;
  onClose: () => void;
  onSave: (firstName: string, lastName: string) => void;
}

export default function UpdateNameDialog(props: UpdateNameDialogProps) {
  const cancelButtonRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      firstName: props.firstName,
      lastName: props.lastName,
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required('First Name is required'),
      lastName: yup.string().required('Last Name is required'),
    }),
    onSubmit: values => {
      props.onSave(values.firstName, values.lastName);
      props.onClose();
    },
  });

  const handleOnCancel = () => {
    formik.resetForm();
    props.onClose();
  };

  return (
    <Modal isOpen={props.isOpen} onClose={handleOnCancel} initialFocus={cancelButtonRef}>
      <div className="sm:flex sm:items-start">
        <h3 className="tw-text-lg tw-leading-6 tw-font-medium tw-text-gray-900">
          Update your name
        </h3>
        <div className="tw-mt-2 tw-max-w-xl tw-text-sm tw-text-gray-500">
          <p>Change the name you want associated with your account.</p>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="tw-grid tw-grid-cols-1 tw-gap-y-6 tw-gap-x-4 tw-grid-cols-6 tw-mt-3"
        >
          <div className="tw-col-span-3">
            <label
              htmlFor="firstName"
              className="tw-block tw-text-sm tw-font-medium tw-text-gray-700"
            >
              First name
            </label>
            <div className="tw-mt-1">
              <Input
                type="text"
                name="firstName"
                id="firstName"
                required
                autoComplete="given-name"
                error={
                  formik.touched.firstName && formik.errors.firstName
                    ? formik.errors.firstName
                    : undefined
                }
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="tw-col-span-3">
            <label
              htmlFor="lastName"
              className="tw-block tw-text-sm tw-font-medium tw-text-gray-700"
            >
              Last name
            </label>
            <div className="tw-mt-1">
              <Input
                type="text"
                name="lastName"
                id="lastName"
                required
                autoComplete="family-name"
                error={
                  formik.touched.lastName && formik.errors.lastName
                    ? formik.errors.lastName
                    : undefined
                }
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="tw-col-span-6">
            <div className="tw-mt-5 sm:tw-mt-4 sm:tw-flex sm:tw-flex-row-reverse">
              <button
                type="submit"
                className="tw-mt-3 tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-border tw-border-transparent tw-shadow-sm tw-font-medium tw-rounded-md tw-text-white tw-bg-secondary hover:tw-bg-secondary-dark focus:tw-outline-none focus:ring-2 focus:tw-ring-offset-2 focus:tw-ring-secondary sm:tw-mt-0 sm:tw-ml-3 sm:tw-w-auto sm:tw-text-sm disabled:tw-opacity-50"
                disabled={!formik.isValid}
              >
                Save
              </button>
              <button
                type="button"
                className="tw-mt-3 tw-w-full tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-shadow-sm tw-px-4 tw-py-2 tw-bg-white tw-text-base tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-secondary sm:tw-mt-0 sm:tw-w-auto sm:tw-text-sm"
                onClick={handleOnCancel}
                ref={cancelButtonRef}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
